import { supabase } from '@/integrations/supabase/client';
import { logError } from '@/lib/error-handler';
import type {
  Project,
  ProjectInsert,
  ProjectUpdate,
  ProjectWithDetails,
  ProjectSummary,
  ProjectSearchOptions,
  ProjectMetrics,
  ProjectProfitability,
  ProjectProgress,
} from '@/types/project.types';
import { enhancedProfitabilityService } from './enhancedProfitabilityService';
import { generateInvoiceFromMilestone, processAutomatedBilling } from '../invoice/invoiceService';
import { teamMemberPaymentService } from './teamMemberPaymentService';
import { expensifyService } from '../integrations/expensifyService';
import { hiveService } from '../integrations/hiveService';

// Core project CRUD operations
export const projectService = {
  // Fetch all projects with optional filtering and pagination
  async fetchProjects(options?: ProjectSearchOptions): Promise<ProjectWithDetails[]> {
    let query = supabase.from('projects').select(`
        *,
        organization:organizations!projects_organization_id_fkey (id, name),
        project_manager:profiles!projects_project_manager_id_fkey (id, first_name, last_name, email),
        client_contact:contacts!projects_client_contact_id_fkey (id, first_name, last_name, email)
      `);

    // Apply filters
    if (options?.filters) {
      const { filters } = options;

      if (filters.status?.length) {
        query = query.in('status', filters.status);
      }

      if (filters.organization_id) {
        query = query.eq('organization_id', filters.organization_id);
      }

      if (filters.project_manager_id) {
        query = query.eq('project_manager_id', filters.project_manager_id);
      }

      if (filters.project_type?.length) {
        query = query.in('type', filters.project_type);
      }

      if (filters.start_date_from) {
        query = query.gte('start_date', filters.start_date_from);
      }

      if (filters.start_date_to) {
        query = query.lte('start_date', filters.start_date_to);
      }

      if (filters.budget_min !== undefined) {
        query = query.gte('budget', filters.budget_min);
      }

      if (filters.budget_max !== undefined) {
        query = query.lte('budget', filters.budget_max);
      }
    }

    // Apply search
    if (options?.query) {
      query = query.or(`name.ilike.%${options.query}%,project_code.ilike.%${options.query}%`);
    }

    // Apply sorting
    const sortBy = options?.sort_by || 'created_at';
    const sortOrder = options?.sort_order || 'desc';
    query = query.order(sortBy, { ascending: sortOrder === 'asc' });

    // Apply pagination
    if (options?.limit) {
      query = query.limit(options.limit);
    }
    if (options?.offset) {
      query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
    }

    const { data, error } = await query;

    if (error) {
      logError('Error fetching projects:', error);
      throw error;
    }
    return (data || []) as ProjectWithDetails[];
  },

  // Fetch a single project by ID with all related data
  async fetchProjectById(id: string): Promise<ProjectSummary | null> {
    // Fetch basic project data
    const { data: project, error: projectError } = await supabase
      .from('projects')
      .select(
        `
        *,
        organization:organizations!projects_organization_id_fkey (id, name),
        project_manager:profiles!projects_project_manager_id_fkey (id, first_name, last_name, email),
        client_contact:contacts!projects_client_contact_id_fkey (id, first_name, last_name, email)
      `
      )
      .eq('id', id)
      .maybeSingle();

    if (projectError) {
      logError('Error fetching project by ID:', projectError);
      throw projectError;
    }
    if (!project) return null;

    // Fetch enhanced profitability data
    let profitability = null;
    try {
      profitability = await enhancedProfitabilityService.calculateProjectProfitability(id);
    } catch (profitError) {
      // Fallback to basic profitability calculation
      const { data: basicProfitability, error } = await supabase
        .rpc('calculate_project_profitability', { project_uuid: id })
        .single();
      if (!error) profitability = basicProfitability;
    }

    // Fetch progress data
    const { data: progress, error: progressError } = await supabase
      .rpc('get_project_progress', { project_uuid: id })
      .single();

    if (progressError) {
      throw progressError;
    }

    // Fetch recent activities
    const { data: activities, error: activitiesError } = await supabase
      .from('project_activities')
      .select(
        `
        *,
        performed_by_user:profiles!project_activities_performed_by_fkey (id, first_name, last_name, email),
        related_milestone:project_milestones!project_activities_related_milestone_id_fkey (id, title),
        related_expense:project_expenses!project_activities_related_expense_id_fkey (id, description, amount)
      `
      )
      .eq('project_id', id)
      .order('activity_date', { ascending: false })
      .limit(10);

    if (activitiesError) {
      throw activitiesError;
    }

    // Count team members
    const { count: teamMemberCount, error: teamError } = await supabase
      .from('project_team_members')
      .select('*', { count: 'exact', head: true })
      .eq('project_id', id)
      .eq('status', 'active');

    if (teamError) {
      throw teamError;
    }

    // Calculate total time logged
    const { data: timeData, error: timeError } = await supabase
      .from('project_time_entries')
      .select('hours_logged')
      .eq('project_id', id);

    if (timeError) {
      throw timeError;
    }

    const totalTimeLogged =
      timeData?.reduce((sum, entry) => sum + (entry.hours_logged || 0), 0) || 0;

    // Calculate total expenses
    const { data: expenseData, error: expenseError } = await supabase
      .from('project_expenses')
      .select('amount')
      .eq('project_id', id)
      .neq('reimbursement_status', 'rejected');

    if (expenseError) {
      throw expenseError;
    }

    const totalExpenses =
      expenseData?.reduce((sum, expense) => sum + (expense.amount || 0), 0) || 0;

    return {
      ...project,
      profitability: profitability as ProjectProfitability,
      progress: progress as ProjectProgress,
      recent_activities: activities || [],
      team_member_count: teamMemberCount || 0,
      total_time_logged: totalTimeLogged,
      total_expenses: totalExpenses,
    } as ProjectSummary;
  },

  // Create a new project
  async createProject(project: ProjectInsert): Promise<Project> {
    const { data, error } = await supabase.from('projects').insert(project).select().single();

    if (error) throw error;

    // Log activity
    await this.logActivity({
      project_id: data.id,
      activity_type: 'project_created',
      title: 'Project Created',
      description: `Project "${data.name}" was created`,
    });

    return data;
  },

  // Update a project
  async updateProject(id: string, update: ProjectUpdate): Promise<Project> {
    const { data, error } = await supabase
      .from('projects')
      .update(update)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    // Log activity
    await this.logActivity({
      project_id: id,
      activity_type: 'project_updated',
      title: 'Project Updated',
      description: `Project details were updated`,
    });

    return data;
  },

  // Delete a project
  async deleteProject(id: string): Promise<void> {
    const { error } = await supabase.from('projects').delete().eq('id', id);

    if (error) throw error;
  },

  // Generate project code using database function
  async generateProjectCode(organizationId: string): Promise<string> {
    const { data, error } = await supabase.rpc('generate_project_code', { org_id: organizationId });

    if (error) throw error;
    return data;
  },

  // Get contacts for organization dropdown
  async getOrganizationContacts(organizationId: string): Promise<any[]> {
    const { data, error } = await supabase.rpc('get_organization_contacts', {
      org_id: organizationId,
    });

    if (error) throw error;
    return data || [];
  },

  // Get project managers for dropdown
  async getProjectManagers(): Promise<any[]> {
    const { data, error } = await supabase.rpc('get_project_managers');

    if (error) throw error;
    return data || [];
  },

  // Get organizations with details for dropdown
  async getOrganizationsWithDetails(): Promise<any[]> {
    const { data, error } = await supabase.rpc('get_organizations_with_details');

    if (error) throw error;
    return data || [];
  },

  // Get project metrics for dashboard
  async getProjectMetrics(organizationId?: string): Promise<ProjectMetrics> {
    let baseQuery = supabase.from('projects').select('*', { count: 'exact' });

    if (organizationId) {
      baseQuery = baseQuery.eq('organization_id', organizationId);
    }

    // Get total projects
    const { count: totalProjects } = await baseQuery;

    // Get active projects
    let activeQuery = supabase
      .from('projects')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'active');
    if (organizationId) {
      activeQuery = activeQuery.eq('organization_id', organizationId);
    }
    const { count: activeProjects } = await activeQuery;

    // Get completed projects
    let completedQuery = supabase
      .from('projects')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'completed');
    if (organizationId) {
      completedQuery = completedQuery.eq('organization_id', organizationId);
    }
    const { count: completedProjects } = await completedQuery;

    // Get revenue from completed milestones
    const revenueQuery = supabase
      .from('project_milestones')
      .select('amount, project:projects!project_milestones_project_id_fkey(organization_id)')
      .eq('status', 'completed');

    const { data: revenueData } = await revenueQuery;

    const totalRevenue =
      revenueData?.reduce((sum, item) => {
        // Filter by organization if needed
        if (organizationId && item.project && item.project.organization_id !== organizationId) {
          return sum;
        }
        return sum + (item.amount || 0);
      }, 0) || 0;

    // Get total expenses
    const expenseQuery = supabase
      .from('project_expenses')
      .select('amount, project:projects!project_expenses_project_id_fkey(organization_id)')
      .neq('reimbursement_status', 'rejected');

    const { data: expenseData } = await expenseQuery;

    const totalExpenses =
      expenseData?.reduce((sum, item) => {
        // Filter by organization if needed
        if (organizationId && item.project && item.project.organization_id !== organizationId) {
          return sum;
        }
        return sum + (item.amount || 0);
      }, 0) || 0;

    // Calculate total profit and average margin
    const totalProfit = totalRevenue - totalExpenses;
    const averageProfitMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;

    // Get overdue milestones
    let overdueMilestonesQuery = supabase
      .from('project_milestones')
      .select('*, project:projects!project_milestones_project_id_fkey!inner(organization_id)', {
        count: 'exact',
        head: true,
      })
      .in('status', ['pending', 'in_progress'])
      .lt('due_date', new Date().toISOString());

    if (organizationId) {
      overdueMilestonesQuery = overdueMilestonesQuery.eq('project.organization_id', organizationId);
    }

    const { count: overdueMilestones } = await overdueMilestonesQuery;

    // Get pending expenses
    let pendingExpensesQuery = supabase
      .from('project_expenses')
      .select('*, project:projects!project_expenses_project_id_fkey!inner(organization_id)', {
        count: 'exact',
        head: true,
      })
      .eq('reimbursement_status', 'pending');

    if (organizationId) {
      pendingExpensesQuery = pendingExpensesQuery.eq('project.organization_id', organizationId);
    }

    const { count: pendingExpenses } = await pendingExpensesQuery;

    // Get total time logged
    let timeQuery = supabase
      .from('project_time_entries')
      .select(
        'hours_logged, project:projects!project_time_entries_project_id_fkey!inner(organization_id)'
      );

    if (organizationId) {
      timeQuery = timeQuery.eq('project.organization_id', organizationId);
    }

    const { data: timeData } = await timeQuery;

    const totalTimeLogged = timeData?.reduce((sum, item) => sum + (item.hours_logged || 0), 0) || 0;

    return {
      total_projects: totalProjects || 0,
      active_projects: activeProjects || 0,
      completed_projects: completedProjects || 0,
      total_revenue: totalRevenue,
      total_expenses: totalExpenses,
      total_profit: totalProfit,
      average_profit_margin: averageProfitMargin,
      overdue_milestones: overdueMilestones || 0,
      pending_expenses: pendingExpenses || 0,
      total_time_logged: totalTimeLogged,
    };
  },

  // Log project activity
  async logActivity(activity: {
    project_id: string;
    activity_type: string;
    title: string;
    description?: string;
    related_milestone_id?: string;
    related_expense_id?: string;
    metadata?: Record<string, any>;
  }): Promise<void> {
    const { data: userData } = await supabase.auth.getUser();

    const { error } = await supabase.from('project_activities').insert({
      ...activity,
      performed_by: userData?.user?.id,
    });

    if (error) {
      throw error;
    }
  },

  // Enhanced financial methods
  async getEnhancedProfitability(projectId: string) {
    return await enhancedProfitabilityService.calculateProjectProfitability(projectId);
  },

  async getProjectFinancialMetrics(projectId: string) {
    return await enhancedProfitabilityService.getProjectFinancialMetrics(projectId);
  },

  async getCOGSAnalysis(projectId: string) {
    return await teamMemberPaymentService.getCOGSAnalysis(projectId);
  },

  async checkBudgetAlerts(projectId: string) {
    return await enhancedProfitabilityService.checkBudgetAlerts(projectId);
  },

  // Automated billing methods
  async generateInvoiceFromMilestone(milestoneId: string) {
    return await generateInvoiceFromMilestone(milestoneId);
  },

  async processAutomatedBilling(organizationId?: string) {
    return await processAutomatedBilling(organizationId);
  },

  // Team payment methods
  async createTeamMemberPayment(paymentRequest: any) {
    return await teamMemberPaymentService.createPaymentRequest(paymentRequest);
  },

  async getPendingPayments(organizationId?: string) {
    return await teamMemberPaymentService.getPendingApprovals(organizationId);
  },

  // External integrations
  async initializeExpensifyIntegration(organizationId: string) {
    try {
      await expensifyService.initialize(organizationId);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async importExpensesFromExpensify(reportId: string, projectId: string) {
    try {
      await expensifyService.initialize(
        // Get organization ID from project
        (await this.fetchProjectById(projectId))?.organization?.id || ''
      );
      return await expensifyService.importExpensesFromReport(reportId, projectId);
    } catch (error) {
      throw error;
    }
  },

  async initializeHiveIntegration(organizationId: string) {
    try {
      await hiveService.initialize(organizationId);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async createHiveProject(projectId: string) {
    try {
      const project = await this.fetchProjectById(projectId);
      if (!project) throw new Error('Project not found');

      await hiveService.initialize(project.organization?.id || '');
      return await hiveService.createHiveProject(project);
    } catch (error) {
      throw error;
    }
  },

  async importTimeEntriesFromHive(projectId: string, startDate?: string, endDate?: string) {
    try {
      const project = await this.fetchProjectById(projectId);
      if (!project) throw new Error('Project not found');

      await hiveService.initialize(project.organization?.id || '');
      return await hiveService.importTimeEntries(projectId, startDate, endDate);
    } catch (error) {
      throw error;
    }
  },

  async getProjectProductivityAnalytics(projectId: string, startDate: string, endDate: string) {
    try {
      const project = await this.fetchProjectById(projectId);
      if (!project) throw new Error('Project not found');

      await hiveService.initialize(project.organization?.id || '');
      return await hiveService.getProductivityAnalytics(projectId, startDate, endDate);
    } catch (error) {
      return {
        total_hours: 0,
        billable_hours: 0,
        productivity_score: 0,
        team_utilization: [],
      };
    }
  },

  // Enhanced project creation with integrations
  async createEnhancedProject(
    projectData: ProjectInsert & {
      create_hive_project?: boolean;
      setup_expensify_integration?: boolean;
      auto_generate_milestones?: boolean;
      milestone_template?: string;
    }
  ): Promise<Project> {
    // Create the base project
    const project = await this.createProject(projectData);

    try {
      // Initialize integrations if requested
      if (projectData.create_hive_project && projectData.organization_id) {
        try {
          await this.initializeHiveIntegration(projectData.organization_id);
          await hiveService.createHiveProject(project);
        } catch (error) {
          // Error handling omitted intentionally
        }
      }

      if (projectData.setup_expensify_integration && projectData.organization_id) {
        try {
          await this.initializeExpensifyIntegration(projectData.organization_id);
        } catch (error) {
          // Error handling omitted intentionally
        }
      }

      // Auto-generate milestones if requested
      if (projectData.auto_generate_milestones && projectData.milestone_template) {
        await this.generateMilestonesFromTemplate(project.id, projectData.milestone_template);
      }
    } catch (error) {
      // Don't fail the project creation, just log the error
    }

    return project;
  },

  async generateMilestonesFromTemplate(projectId: string, templateName: string) {
    // Milestone templates for common project types
    const templates = {
      website_redesign: [
        { title: 'Discovery & Planning', percentage: 20, days_offset: 7 },
        { title: 'Design & Mockups', percentage: 30, days_offset: 21 },
        { title: 'Development', percentage: 40, days_offset: 45 },
        { title: 'Testing & Launch', percentage: 10, days_offset: 60 },
      ],
      mobile_app: [
        { title: 'Requirements & Wireframes', percentage: 15, days_offset: 10 },
        { title: 'UI/UX Design', percentage: 25, days_offset: 25 },
        { title: 'Backend Development', percentage: 30, days_offset: 45 },
        { title: 'Frontend Development', percentage: 25, days_offset: 65 },
        { title: 'Testing & Deployment', percentage: 5, days_offset: 75 },
      ],
      marketing_campaign: [
        { title: 'Strategy & Planning', percentage: 30, days_offset: 7 },
        { title: 'Creative Development', percentage: 40, days_offset: 21 },
        { title: 'Campaign Launch', percentage: 20, days_offset: 35 },
        { title: 'Analysis & Reporting', percentage: 10, days_offset: 45 },
      ],
    };

    const template = templates[templateName as keyof typeof templates];
    if (!template) return;

    // Get project details
    const project = await this.fetchProjectById(projectId);
    if (!project) return;

    const startDate = new Date(project.start_date || new Date());
    const totalBudget = project.budget || 0;

    // Create milestones
    for (const milestone of template) {
      const dueDate = new Date(startDate);
      dueDate.setDate(dueDate.getDate() + milestone.days_offset);

      await supabase.from('project_milestones').insert({
        project_id: projectId,
        title: milestone.title,
        amount: (totalBudget * milestone.percentage) / 100,
        due_date: dueDate.toISOString().split('T')[0],
        status: 'pending',
        progress_percentage: 0,
        created_by: (await supabase.auth.getUser()).data.user?.id,
      });
    }

    await this.logActivity({
      project_id: projectId,
      activity_type: 'milestones_generated',
      title: 'Milestones Generated',
      description: `Generated ${template.length} milestones from ${templateName} template`,
    });
  },
};
